"use client";

import { ArrowLeft, Clock } from "lucide-react";

import BlogAuthor from "../BlogAuthor";
import BlogViews from "../BlogViews";
import { HERO_WRAPPER_BASE } from "./heroWrapperClasses";
import Link from "next/link";
import RenderBlogTitle from "../RenderBlogTitle";
import StoneLogo from "../StoneLogo";
import { WPAPIPostGraphQL } from "@/types";
import { cn } from "@/lib/utils";
import { useDataContext } from "@/app/_context";

interface ArticleHeroProps {
  post: WPAPIPostGraphQL;
  views: number | null;
  readingTime: number | null;
  navHidden?: boolean;
}

export default function ArticleHero({
  post,
  views,
  readingTime,
  navHidden,
}: ArticleHeroProps) {
  const { setIsHeroHovered, hoverLeaveTimerRef } = useDataContext();

  const handleMouseEnter = () => {
    if (hoverLeaveTimerRef.current) {
      clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
    setIsHeroHovered(true);
  };

  const handleMouseLeave = () => {
    hoverLeaveTimerRef.current = setTimeout(() => setIsHeroHovered(false), 300);
  };

  const { title } = post.node;
  const imageUrl = post.image?.sourceUrl;
  const bgImage = title.toLocaleLowerCase().includes("morsi")
    ? `url(https://www.sebastiangrantcdn.com/stone/wp-content/uploads/2025/10/Morsi.jpg)`
    : `url(${imageUrl})`;

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(HERO_WRAPPER_BASE, "-mt-14 md:-mt-20")}
    >
      {/* photo layer */}

      <div
        style={{ backgroundImage: bgImage }}
        className="absolute inset-0 bg-cover bg-center"
      />

      {/* gradient overlay — seals edge gaps, darkens for text */}
      <div className="absolute inset-0 scale-[1.1] bg-linear-to-t from-black/95 via-black/50 to-black/5 pointer-events-none" />

      {/* content — pinned high enough to stay in the opaque zone */}
      <div className="absolute bottom-[18%] left-0 right-0 px-6 md:px-12">
        <div className="flex items-end justify-between gap-8">
          <div className="flex min-w-0 flex-col gap-3">
            <RenderBlogTitle title={title}>{title}</RenderBlogTitle>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 font-Cabin text-sm text-white/75">
              <BlogAuthor post={post} showDate recentPostMode />

              {(views ?? 0) > 0 && (
                <BlogViews views={views!} className="text-white/75" />
              )}

              {readingTime && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {readingTime} min read
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <Link
        href="/"
        aria-label="Stone home"
        className={`absolute left-6 top-5 z-20 transition-opacity duration-500 ${
          navHidden
            ? "opacity-70 hover:opacity-100"
            : "pointer-events-none opacity-0"
        }`}
      >
        <StoneLogo className="w-24 max-w-24 invert drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]" />
      </Link>

      <div className="right-0 md:top-24 top-22 md:m-24 m-8 mr-0 md:mr-24 absolute">
        <Link href="/blog" className="w-12 h-6 grid place-items-center">
          <ArrowLeft className="md:w-12 md:h-12 h-8 w-8 text-white" />
        </Link>
      </div>
    </div>
  );
}
